import {
  Backdrop,
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import Label from "../../../components/Label";
import { parseDate, parseDateTime } from "../../../utils/tableFunctions";
import { getPendingMerchantPayoutInfoDetails } from "../../../services/services";
import { Clear } from "@mui/icons-material";
import HeaderHelpPage from "../../../components/HeaderHelpPage";
import { linkTextStyle, styles } from "../../../utils/styles";
import { handleOpenLink } from "../../../utils/appUtils";
import PendingDetailsComparison from "../../../components/PendingDetailsComparison";

const FeesDisplay = ({ currenciesData }) => {
  if (!currenciesData || currenciesData.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        (No Service Fees configured)
      </Typography>
    );
  }

  return (
    <TableContainer component={Paper} elevation={0}>
      <Table size="small">
        <TableBody>
          {/* Header Row Fees */}
          <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
            <TableCell sx={{ fontWeight: 'bold', padding: '4px 8px' }}>Currency</TableCell>
            <TableCell sx={{ fontWeight: 'bold', padding: '4px 8px' }}>Transaction Fee</TableCell>
            <TableCell sx={{ fontWeight: 'bold', padding: '4px 8px' }}>Refund Fee</TableCell>
            <TableCell sx={{ fontWeight: 'bold', padding: '4px 8px' }}>Chargeback Fee</TableCell>
          </TableRow>

          {/* Data Rows */}
          {currenciesData.map((fee, index) => (
            <TableRow key={index}>
              <TableCell sx={{ padding: '4px 8px' }}>{fee.currency}</TableCell>
              <TableCell sx={{ padding: '4px 8px' }}>{fee.transaction_fee || '0'}</TableCell>
              <TableCell sx={{ padding: '4px 8px' }}>{fee.refund_fee || '0'}</TableCell>
              <TableCell sx={{ padding: '4px 8px' }}>{fee.chargeback_fee || '0'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const MERCHANT_PAYOUT_FIELDS = [
  { key: "merchant", label: "Merchant" },
  {
    key: "terminal",
    label: "Terminal",
    render: (value) => value?.terminal_id || "N/A",
  },
  { key: "payment_brand", label: "Payment Brand" },
  { key: "payment_host", label: "Payment Host" },
  { key: "payment_cycle", label: "Payment Cycle" },
  { key: "is_active", label: "Active" },
  {
    key: "currencies_data",
    label: "Service Fees",
    render: (value) => <FeesDisplay currenciesData={value} />,
  },
];

const MerchantPayoutInfoDetails = ({ detailsData, itemId, isViewHistory }) => {
  const [merchantPayoutInfoDetails, setMerchantPayoutInfoDetails] = useState();
  const [loaded, setLoaded] = useState();
  const { id = itemId } = useParams();
  const useQuery = () => {
    return new URLSearchParams(useLocation().search);
  };
  const action = useQuery().get("action") || (detailsData && "true") || (isViewHistory && "true");
  // Every maker-and-checker view: the pending dialog and the history dialog.
  const isCheckerView = Boolean(action);
  const isHistoryView = Boolean(isViewHistory) || (!detailsData && Boolean(action));
  const mobile = useMediaQuery((theme) => theme.breakpoints.down("lg"));

  useEffect(() => {
    if (!detailsData && id) {
      getPendingMerchantPayoutInfoDetails(id, setMerchantPayoutInfoDetails, setLoaded);
      return;
    }
    setMerchantPayoutInfoDetails(detailsData);
    setLoaded(true);
  }, [action, detailsData, id]);

  const handleBack = () => window.close();

  const InfoColumn = () => {
    if (!merchantPayoutInfoDetails) return null;
    const { currencies_data } = merchantPayoutInfoDetails;
    return (
      <Grid item xs={6} md={6}>
        <Box
          sx={{
            height: "100%",
            p: 3,
            border: "1px solid #999",
            borderRadius: 2,
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">Merchant Name</Typography>
            </Grid>
            <Grid
              item
              xs={6}
              md={7}
              sx={{ ...linkTextStyle }}
              onClick={() => handleOpenLink(`/3/app/merchant-details/${merchantPayoutInfoDetails.merchant.id}`)}
            >
              {merchantPayoutInfoDetails.merchant.name}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">Terminal ID</Typography>
            </Grid>
            <Grid
              item
              xs={6}
              md={7}
              sx={{ ...linkTextStyle }}
              onClick={() => handleOpenLink(`/3/app/terminal-details/${merchantPayoutInfoDetails.terminal.id}/`)}
            >
              {merchantPayoutInfoDetails.terminal.terminal_id}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">Payment Brand</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {merchantPayoutInfoDetails?.payment_brand?.name}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">Payment Host</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {merchantPayoutInfoDetails?.payment_host?.name}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">Payment Cycle</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {merchantPayoutInfoDetails.payment_cycle}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">Active</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              <Label color={merchantPayoutInfoDetails.is_active ? "success" : "error"}>
                {merchantPayoutInfoDetails.is_active ? "YES" : "NO"}
              </Label>
            </Grid>
            <Grid item xs={6} md={5} sx={{ alignSelf: 'flex-start', pt: '16px !important' }}>
              <Typography variant="subtitle1">
                Service Fees
              </Typography>
            </Grid>
            <Grid item xs={6} md={7} sx={{ pt: '16px !important' }}>
              <FeesDisplay currenciesData={currencies_data} />
            </Grid>
            {/* <Grid item xs={6} md={7}>
              {merchantPayoutInfoDetails.transaction_fee_amount}
            </Grid> */}
            
          </Grid>
        </Box>
      </Grid>
    );
  }

  const DateColumn = () => {
    const requestedOnLabel = isHistoryView ? "Requested on" : "Created on";
    const requestedByLabel = isHistoryView ? "Requested by" : "Created by";
    const reviewedOnLabel = isHistoryView ? "Reviewed on" : "Approved on";
    const reviewedByLabel = isHistoryView ? "Reviewed by" : "Approved by";
    const formatTimestamp = isHistoryView ? parseDateTime : parseDate;

    return (
      <Grid item xs={6} md={6}>
        <Box
          sx={{
            height: "100%",
            p: 3,
            border: "1px solid #999",
            borderRadius: 2,
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">
                {action === "true" ? "Approved" : "Active Now"}
              </Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {action === "true" ? (
                <Label
                  variant="ghost"
                  color={merchantPayoutInfoDetails.is_approved ? "success" : "error"}
                >
                  {merchantPayoutInfoDetails.is_approved ? "YES" : "NO"}
                </Label>
              ) : (
                <Label
                  variant="ghost"
                  color={merchantPayoutInfoDetails.is_active ? "success" : "error"}
                >
                  {merchantPayoutInfoDetails.is_active ? "YES" : "NO"}
                </Label>
              )}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">{requestedOnLabel}</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {merchantPayoutInfoDetails.created_at
                ? formatTimestamp(merchantPayoutInfoDetails.created_at)
                : "N/A"}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">{requestedByLabel}</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {merchantPayoutInfoDetails.created_by
                ? merchantPayoutInfoDetails.created_by.email
                : "N/A"}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">{reviewedOnLabel}</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {merchantPayoutInfoDetails.approved_at
                ? formatTimestamp(merchantPayoutInfoDetails.approved_at)
                : "N/A"}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">{reviewedByLabel}</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {merchantPayoutInfoDetails.approved_by
                ? merchantPayoutInfoDetails.approved_by.email
                : "N/A"}
            </Grid>
            {!action && (
              <>
                <Grid item xs={6} md={5}>
                  <Typography variant="subtitle1">Updated on</Typography>
                </Grid>
                <Grid item xs={6} md={7}>
                  {parseDate(merchantPayoutInfoDetails.updated_at)}
                </Grid>
                <Grid item xs={6} md={5}>
                  <Typography variant="subtitle1">Updated by</Typography>
                </Grid>
                <Grid item xs={6} md={7}>
                  {merchantPayoutInfoDetails.updated_by}
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      </Grid>
    );
  }

  return (
    <Container>
      {!detailsData && !itemId && (
        <Stack
          direction="row"
          sx={{
            ...styles.flexBetween,
            mb: 2,
          }}
        >
          <HeaderHelpPage title="Merchant Payout Info Details" variant={mobile ? "h5" : "h3"} />

          <Button
            variant="outlined"
            onClick={handleBack}
            endIcon={<Clear />}
          >
            Back
          </Button>
        </Stack>
      )}
      {loaded && action === "true" && (
        <Typography variant="h6" sx={{ mb: 2 }}>
          Action: {merchantPayoutInfoDetails.action && merchantPayoutInfoDetails.action}
        </Typography>
      )}
      {loaded && Boolean(merchantPayoutInfoDetails?.original_unavailable) && (
        <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
          Before values are not available for this request because it was
          submitted before change logging was in place.
        </Typography>
      )}
      {loaded ? (
        <>
          <Card sx={{ p: 3 }}>
            <Grid
              container
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 2, sm: 2, md: 2 }}
              sx={{ mt: 5 }}
            >
            {isCheckerView && !merchantPayoutInfoDetails.original_unavailable ? (
              <>
                <PendingDetailsComparison
                  data={merchantPayoutInfoDetails}
                  original={merchantPayoutInfoDetails.original}
                  fields={MERCHANT_PAYOUT_FIELDS}
                />
                <DateColumn />
              </>
            ) : (
              <>
                {action && <DateColumn />}
                <InfoColumn />
                {!action && <DateColumn />}
              </>
            )}
            </Grid>
            {merchantPayoutInfoDetails.reject_message && (
              <Typography variant="subtitle1" sx={{ mt: 2, display: "flex" }}>
                Reason for rejection:
                <Typography sx={{ ml: 1 }}>
                  {" "}
                  {merchantPayoutInfoDetails.reject_message}
                </Typography>
              </Typography>
            )}
            {merchantPayoutInfoDetails.action_message && (
              <Typography variant="subtitle1" sx={{ mt: 2, display: "flex" }}>
                Action message:
                <Typography sx={{ ml: 1 }}>
                  {" "}
                  {merchantPayoutInfoDetails.action_message}
                </Typography>
              </Typography>
            )}
          </Card>
        </>
      ) : (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
    </Container>
  );
};

export default MerchantPayoutInfoDetails;
